import {gql} from "@apollo/client";
import {Connection} from "../common/Connection";
import {AuthPolicy} from "./models/AuthPolicy";
import {AuthPolicyUsage} from "./models/AuthPolicyUsage";
import {AuthAggregatedPolicy} from "./models/AuthAggregatedPolicy";
import {DeletePolicyUsagePayload} from "./models/DeletePolicyUsagePayload";
import {PutPolicyUsagePayload} from "./models/PutPolicyUsagePayload";
import {DeletePolicyPayload} from "./models/DeletePolicyPayload";
import {PutPolicyPayload} from "./models/PutPolicyPayload";
import {AuthDifferenceSet} from "./models/AuthDifferenceSet";

export interface AuthQueries {
	readonly analyzeChanges: AuthDifferenceSet;
	readonly policies: Connection<AuthPolicy>;
	readonly policyUsages: Connection<AuthPolicyUsage>;
	readonly rules: AuthAggregatedPolicy;
	readonly userRules: AuthAggregatedPolicy;
	
	readonly putPolicy: PutPolicyPayload;
	readonly deletePolicy: DeletePolicyPayload;
	readonly putPolicyUsage: PutPolicyUsagePayload;
	readonly deletePolicyUsage: DeletePolicyUsagePayload;
}

export const AUTH_ANALYZE_CHANGES = gql`
	query {
		analyzeChanges {
			stages
			policies {
				krn
				tenant
				name
				rules {
					effect
					actions
					resources
				}
				subPolicies
				isSynchronized
				id
			}
			policyUsages {
				krn
				tenant
				name
				userName
				organisationId
				roleName
				policies
				isSynchronized
				id
			}
		}
	}
`;

export const POLICIES = gql`
	query (
		$first: Int
		$after: String
		$where: AuthPolicyFilterInput
		$order: [AuthPolicySortInput!]
	) {
		policies(
			first: $first
			after: $after
			where: $where
			order: $order
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				krn
				tenant
				name
				rules {
					effect
					actions
					resources
				}
				subPolicies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
		}
	}
`;

export const POLICIES_INDEX = gql`
	query (
		$where: AuthPolicyFilterInput
	) {
		policies(
            where: $where
			order: { name: ASC }
		) {
			nodes {
				id
				krn
				tenant
				name
			}
		}
	}
`;

export const POLICY_BY_KRN = gql`
	query (
		$krn: String!
	) {
		policies(
			first: 1
			where: {
				krn: { eq: $krn }
			}
		) {
			nodes {
				krn
				tenant
				name
				rules {
					effect
					actions
					resources
				}
				subPolicies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
		}
	}
`;

export const POLICY_USAGES_BY_POLICY_KRN = gql`
	query (
		$krn: String!
	) {
		policyUsages(
			where: {
				policies: { some: { eq: $krn } }
			}
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				krn
				tenant
				name
				userName
				organisationId
				roleName
				policies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
		}
	}
`;

export const POLICY_USAGES = gql`
	query (
		$first: Int
		$after: String
		$where: AuthPolicyUsageFilterInput
		$order: [AuthPolicyUsageSortInput!]
	) {
		policyUsages(
			first: $first
			after: $after
			where: $where
			order: $order
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				krn
				tenant
				name
				userName
				organisationId
				roleName
				policies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
		}
	}
`;

export const POLICY_USAGES_INDEX = gql`
	query {
		policyUsages {
			nodes {
				krn
			}
		}
	}
`;

export const RULES = gql`
	query (
		$searchParams: AuthPolicySearchParamsInput!
	) {
		rules(
			searchParams: $searchParams
		) {
			rules {
				effect
				actions
				resources
			}
		}
	}
`;

export const USER_RULES = gql`
	query {
		userRules {
			rules {
				effect
				actions
				resources
			}
		}
	}
`;

export const PUT_POLICY = gql`
	mutation (
		$policy: AuthPolicyInput!
        $stage: String
	) {
		putPolicy(
			input: {
				policy: $policy,
				stage: $stage
			}
		) {
			authPolicy {
				krn
				tenant
				name
				rules {
					effect
					actions
					resources
				}
				subPolicies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
			errors {
				... on IGraphQlErrorBase {
					type
					message
				}
				... on InvalidInputError {
					type
					message
					fieldName
				}
				... on NotAuthenticatedError {
					type
					message
					reason
				}
				... on NotAuthorizedError {
					type
					message
					user
					action
					resource
				}
			}
		}
	}
`;

export const DELETE_POLICY = gql`
	mutation (
		$krn: String!
		$stage: String
	) {
		deletePolicy(
			input: {
				krn: $krn
				stage: $stage
			}
		) {
			authPolicy {
				krn
				tenant
				name
				rules {
					effect
					actions
					resources
				}
				subPolicies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
			errors {
				... on IGraphQlErrorBase {
					type
					message
				}
				... on NotAuthenticatedError {
					type
					message
					reason
				}
				... on NotAuthorizedError {
					type
					message
					user
					action
					resource
				}
			}
		}
	}
`;

export const PUT_POLICY_USAGE = gql`
	mutation (
		$policyUsage: AuthPolicyUsageInput!
		$stage: String
	) {
		putPolicyUsage(
			input: {
				stage: $stage
				policyUsage: $policyUsage
			}
		) {
			authPolicyUsage {
				krn
				tenant
				name
				userName
				organisationId
				roleName
				policies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
			errors {
				... on IGraphQlErrorBase {
					type
					message
				}
				... on InvalidInputError {
					type
					message
					fieldName
				}
				... on NotAuthenticatedError {
					type
					message
					reason
				}
				... on NotAuthorizedError {
					type
					message
					user
					action
					resource
				}
			}
		}
	}
`;

export const DELETE_POLICY_USAGE = gql`
	mutation (
		$krn: String!
		$stage: String
	) {
		deletePolicyUsage(
			input: {
				krn: $krn
				stage: $stage
			}
		) {
			authPolicyUsage {
				krn
				tenant
				name
				userName
				organisationId
				roleName
				policies
				isSynchronized
				id
				version
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
			errors {
				... on IGraphQlErrorBase {
					type
					message
				}
				... on NotAuthenticatedError {
					type
					message
					reason
				}
				... on NotAuthorizedError {
					type
					message
					user
					action
					resource
				}
			}
		}
	}
`;
